// src/components/GitHubOverview.jsx

import React from "react";
import { FaStar, FaCodeBranch, FaGithub, FaUsers } from "react-icons/fa";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { motion } from "framer-motion";

const GitHubOverview = ({
  profileStats,
  contributionScore,
  contributionGrade,
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative mb-12"
    >
      {/* Glass morphism container */}
      <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8 shadow-2xl">
        <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          GitHub Overview
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Profile Stats */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
          >
            <h3 className="text-2xl font-semibold mb-6 flex items-center text-white">
              <FaGithub className="mr-3 text-blue-400" aria-hidden="true" />{" "}
              GitHub Stats
            </h3>
            <ul className="space-y-4">
              <li className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                <div className="flex items-center">
                  <FaStar
                    className="text-yellow-400 mr-3 text-xl"
                    aria-hidden="true"
                  />
                  <span className="text-gray-300">Total Stars</span>
                </div>
                <strong className="text-2xl font-bold text-yellow-400">
                  {profileStats.totalStars}
                </strong>
              </li>
              <li className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                <div className="flex items-center">
                  <FaCodeBranch
                    className="text-green-400 mr-3 text-xl"
                    aria-hidden="true"
                  />
                  <span className="text-gray-300">Repositories</span>
                </div>
                <strong className="text-2xl font-bold text-green-400">
                  {profileStats.publicRepos}
                </strong>
              </li>
              <li className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                <div className="flex items-center">
                  <FaUsers
                    className="text-blue-400 mr-3 text-xl"
                    aria-hidden="true"
                  />
                  <span className="text-gray-300">Followers</span>
                </div>
                <strong className="text-2xl font-bold text-blue-400">
                  {profileStats.followers}
                </strong>
              </li>
            </ul>
          </motion.div>

          {/* Contribution Grade */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-lg hover:shadow-purple-500/20 transition-all duration-300 flex flex-col items-center justify-center"
          >
            <h3 className="text-2xl font-semibold mb-6 text-white text-center">
              Contribution Grade
            </h3>
            <div className="flex flex-col items-center">
              <div
                className="relative w-40 h-40 mb-4"
                aria-label="Contribution Grade"
              >
                <CircularProgressbar
                  value={contributionScore}
                  styles={buildStyles({
                    textColor: "transparent",
                    pathColor:
                      contributionGrade === "A+"
                        ? "#FFD700" // Gold
                        : contributionGrade === "A"
                          ? "#4CAF50" // Green
                          : contributionGrade === "A-"
                            ? "#8BC34A" // Light Green
                            : contributionGrade === "B+"
                              ? "#CDDC39" // Lime
                              : contributionGrade === "B"
                                ? "#FFEB3B" // Yellow
                                : contributionGrade === "B-"
                                  ? "#FFC107" // Amber
                                  : contributionGrade === "C+"
                                    ? "#FF9800" // Orange
                                    : contributionGrade === "C"
                                      ? "#FF5722" // Deep Orange
                                      : contributionGrade === "C-"
                                        ? "#F44336" // Red
                                        : contributionGrade === "D+"
                                          ? "#E91E63" // Pink
                                          : contributionGrade === "D"
                                            ? "#9C27B0" // Purple
                                            : contributionGrade === "D-"
                                              ? "#673AB7" // Deep Purple
                                              : "#B0BEC5", // Blue Grey for F
                    trailColor: "rgba(255, 255, 255, 0.1)",
                    pathTransitionDuration: 1.5,
                  })}
                />
                {/* Centered Grade Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-4xl font-bold text-white leading-none">
                    {contributionGrade || "N/A"}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    {contributionScore}%
                  </div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-400">
                  Based on repositories, stars, and followers
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default GitHubOverview;
